import { selectCurrentCourseId } from '../../db/courses/selectors';
import { selectCourseUserProfile } from '../../db/user-profiles/selectors';
import { useAppSelector } from '../../store/hooks';
import { CourseRole } from '../../types';

interface AriaLabelWithEmailId {
  isEmailIdNeeded: true;
  isCourseRoleNeeded: false;
  ariaLabel?: string | ((name?: string, emailId?: string) => string);
}

interface AriaLabelWithCourseRole {
  isEmailIdNeeded: false;
  isCourseRoleNeeded: true;
  ariaLabel?: string | ((name?: string, courseRole?: CourseRole, isRemovedMember?: boolean) => string);
}

interface AriaLabelWithEmailIdAndCourseRole {
  isEmailIdNeeded: true;
  isCourseRoleNeeded: true;
  ariaLabel?:
    | string
    | ((name?: string, emailId?: string, courseRole?: CourseRole, isRemovedMember?: boolean) => string);
}

interface AriaLabelWithoutEmailIdAndCourseRole {
  isEmailIdNeeded: false;
  isCourseRoleNeeded: false;
  ariaLabel?: string | ((name?: string) => string);
}

export type UseCellAriaLabel = {
  avatarId: MongoId;
} & (
  | AriaLabelWithEmailId
  | AriaLabelWithCourseRole
  | AriaLabelWithEmailIdAndCourseRole
  | AriaLabelWithoutEmailIdAndCourseRole
);

const useCellAriaLabel = ({ avatarId, ...props }: UseCellAriaLabel) => {
  const currentCourseId = useAppSelector((state) => selectCurrentCourseId(state));

  const courseUserProfile = useAppSelector((state) => selectCourseUserProfile(state, avatarId));
  const { name, emailId, courseUserDetails } = courseUserProfile || {};
  const courseRole = currentCourseId ? courseUserDetails?.[currentCourseId]?.role : undefined;
  const isRemovedMember = currentCourseId ? courseUserDetails?.[currentCourseId]?.isRemovedMember : undefined;

  if (!props.ariaLabel) {
    return '';
  }

  if (typeof props.ariaLabel !== 'function') {
    return props.ariaLabel;
  }

  if (props.isEmailIdNeeded && props.isCourseRoleNeeded) {
    return props.ariaLabel(name, emailId, courseRole, isRemovedMember);
  }

  if (props.isEmailIdNeeded) {
    return props.ariaLabel(name, emailId);
  }

  if (props.isCourseRoleNeeded) {
    return props.ariaLabel(name, courseRole, isRemovedMember);
  }

  return props.ariaLabel(name);
};

export default useCellAriaLabel;
